<template>
  <div class="best-match-banner d-inline-flex mt-3 mb-4">
    <div class="image-wrapper">
      <!-- mobile best match banner -->
      <div class="d-block d-lg-none best-match-badge">
        <div class="d-block best-match-badge-inner">
          <StaffPickSVG class="icon" />
          <strong>Best match</strong>
        </div>
      </div>
      <!-- Deal image -->
      <div class="image-container">
        <!-- Link overlapping image container -->
        <NuxtLink class="stretched-link float-left" :to="dealUrl" />
        <v-lazy-image
          class="product-image pin-image-top"
          :class="{ 'has-banner': hasBanner(deal),
                    'last-call-margin': isLimitedTimeSale(deal),
          }"
          :src="`${deal.media_url}?width=450&height=300&aspect_ratio=3:2`"
          :src-placeholder="lazyPlaceholderImage"
          :alt="deal.public_name"
        />
        <DealBanner
          class="product-card-deal-banner"
          :deal="deal"
          :is-black-friday-last-call="isBlackFridayLastCall(deal)"
          :is-last-call="isLastCall(deal)"
          :is-limited-time-sale="isLimitedTimeSale(deal)"
        />
        <div v-if="showBadge" class="top-overlay">
          <SumoDayBadge v-if="isSumoDay(deal)" class="campaign-badge sumo-day-badge" />
          <BlackFridayBadge
            v-if="isBlackFriday(deal) || isBlackFridayLastCall(deal)"
            class="campaign-badge black-friday-badge"
          />
        </div>
      </div>
    </div>

    <!-- Deal details -->
    <div class="details-container d-flex flex-column">
      <!-- select badge -->
      <div
        v-if="isSelect"
        class="badge select-badge order-last order-lg-first
          d-inline-flex mt-2 mt-lg-0"
      >
        <SelectSVG width="128" height="11" />
      </div>
      <!-- plus badge -->
      <div
        v-if="isPlusExclusive"
        class="badge badge-listing-type badge-plus-exclusive d-inline-flex
          order-last order-lg-first mt-2 mt-lg-0"
      >
        <PlusExclusiveLogo
          height="11"
        />
      </div>

      <div class="deal-details-container">
        <!-- desktop name -->
        <NuxtLink
          class="section-title d-none d-lg-block"
          :to="dealUrl"
        >
          <h3>{{ deal.public_name }}</h3>
        </NuxtLink>

        <!-- Mobile Name and category-->
        <div
          v-if="showMobileName"
          class="deal-name-container text-truncate d-block d-lg-none"
        >
          <strong :class="categoryName && 'deal-name text-truncate'">
            {{ deal.public_name }}
          </strong>

          <div v-if="categoryName">
            <span class="deal-category-in">in</span>
            <NuxtLink
              class="deal-category-link text-truncate"
              :to="categoryLink"
            >
              {{ categoryName }}
            </NuxtLink>
          </div>
        </div>

        <p class="deal-description my-0">
          {{ deal.card_description }}
        </p>
      </div>

      <div class="price-details-wrapper">
        <!-- Price -->

        <div v-if="showPrice" class="d-block py-2 pt-lg-0 order-2 order-lg-1">
          <span class="pricing-text">
            {{ pricingText }}
          </span>
          <span v-if="showPlanType" class="plan-text text-lowercase">
            /{{ planType }}
          </span>
        </div>

        <div class="details-contianer d-inline-flex order-1 order-lg-2">
          <!-- Button for PDP -->
          <DButton
            class="d-none d-lg-block details-btn text-truncate"
          >
            <NuxtLink :to="dealUrl">
              <span>View details</span>
            </NuxtLink>
          </DButton>

          <!-- Reviews and downloads -->
          <DownloadsAndReviews
            class="downloads-container d-flex align-items-center"
            :deal="deal"
          />
        </div>
      </div>

      <!-- desktop best match banner -->
      <div class="d-lg-block d-none best-match-badge">
        <span>
          <StaffPickSVG class="icon" />
          <strong>Best Match</strong>
        </span>
      </div>
    </div>
  </div>
</template>
<script>
import { DButton } from '@appsumo/dorado-button';
import VLazyImage from 'v-lazy-image';

import BlackFridayBadge from '~/static/campaigns/black-friday-badge.svg?inline';
import SelectSVG from '~/static/appsumo-select.svg?inline';
import StaffPickSVG from '~/static/staff-pick.svg?inline';
import SumoDayBadge from '~/static/sumoday-badge.svg?inline';
import PlusExclusiveLogo from '~/static/appsumo-plus-exclusive.svg?inline';
import DealBanner from '../utilities/DealBanner.vue';
import DownloadsAndReviews from '../skucard/DownloadsAndReviews.vue';

import CampaignsMixin from '~/mixins/campaigns';
import ESDealDataMixin from '~/mixins/es-deal-data';

export default {
  components: {
    BlackFridayBadge,
    DButton,
    DownloadsAndReviews,
    DealBanner,
    PlusExclusiveLogo,
    SelectSVG,
    StaffPickSVG,
    SumoDayBadge,
    VLazyImage,
  },
  mixins: [CampaignsMixin, ESDealDataMixin('deal')],
  props: {
    deal: {
      type: Object,
      required: true,
    },
    variant: {
      type: String,
      required: false,
      default: 'new-no-name',
    },
  },
  computed: {
    showBadge() {
      return this.isSumoDay(this.deal)
        || this.isBlackFriday(this.deal)
        || this.isBlackFridayLastCall(this.deal);
    },
    showPrice() {
      const hasValidPrice = typeof this.deal?.default_plan?.price === 'number';

      return !(
        this.isComingSoonBuyState
        || this.isEndedDeal
        || (this.isLastCallBuyState && !this.userHasPlus)
      ) && hasValidPrice;
    },
    showMobileName() {
      return this.variant === 'new-with-name';
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~/styles/utils/breakpoints';
@import '~/styles/dorado/main';

.badge-listing-type {
  box-shadow: none;
  display: inline-flex;
  max-width: 200px;
  height: 20px;
  width: fit-content;
}

.badge-plus-exclusive {
  background-color: #feefec;

  path {
    // non-dorado color in figma
    fill: #ff5732;
  }
}

.badge.select-badge {
  background-color: #eef9f1;
  box-shadow: none;
  width: 137px;
  height: 20px;
  justify-content: center;

  ::v-deep path {
    // non-dorado color in figma
    fill: #069a2f;
  }
}

.best-match-badge {
  height: 32px;
  margin: 0 0 8px;
  // non-dorado color in figma
  background-color: #6ad989;
  border-radius: 4px;
  justify-content: center;
  padding: 4px;
  font-weight: 600;
  font-size: 14px;

  & strong {
    display: inline-flex;
    margin: 0 8px;
    vertical-align: middle;
  }

  & .best-match-badge-inner {
    margin: 0 auto;
    width: fit-content;
  }
}

.best-match-banner {
  border: 1px solid #6ad989; // Color not in dorado
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  width: 100%;

  .details-container {
    align-items: flex-start;
    display: flex;
    margin: 0;
    padding: 8px;
    position: relative;
    width: 100%;
  }

  p,
  .details-container {
    font-size: 12px;
  }

  .product-card-deal-banner {
    position: initial;
    border-radius: 0 0 4px 4px;
  }

  // Banner overrides - Because we're using the old component
  .banner {
    line-height: 14px;

    /**
    * TODO: Update ready red color in dorado repo.
    *
    * Last call and ending soon uses the ready red color (based on the sku card mockup),
    * however this color has been changed recently and the dorado repo doesn't have
    * the color updated yet.
    *
    */
    &.last-call,
    &.is-ending,
    &.sold-out {
      background-color: var(--brick);
    }

    &.coming-soon {
      background-color: var(--bolt);
    }

    &.limited-time-sale {
      background-color: var(--dark);
    }
  }
}

.deal-details-container {
  align-self: baseline;
}

.deal-category-link,
.deal-category-in,
.deal-name {
  display: inline-block;
  // Needed to add ellipsis to texts
  max-width: 90%;
  vertical-align: middle;
}

.deal-category-link {
  color: $dark;
  text-decoration: underline;
  position: relative;
  z-index: 2;

  &:hover {
    color: $dark;
  }
}

.deal-description {
  /* stylelint-disable-next-line */
  -webkit-box-orient: vertical;
  /* stylelint-disable-next-line */
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 4;
}

.details-btn a {
  color: inherit;

  &:hover {
    text-decoration: none;
  }
}

.image-container {
  cursor: pointer;
  height: auto;
  min-width: 168px;
  position: relative;
  text-decoration: none;
  width: 100%;
}

.image-wrapper {
  max-width: 168px;
  width: 100%;
  height: auto;
}

.product-image {
  min-height: 95px;
  border-radius: 4px;
  width: 100%;

  &.has-banner {
    border-radius: 4px 4px 0 0;
  }
}

.price-details-wrapper {
  margin-top: auto;
}

.pricing-text {
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
}

.plan-text {
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  margin-left: -4px;
}

.section-title {
  color: #021414;
  cursor: pointer;
  display: inline-block;
  line-height: 1.5;
  width: 100%;

  h3 {
    font-weight: 700;
    overflow: hidden;
    text-decoration: none;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 150px;
  }

  &:hover,
  &:focus {
    color: #021414;
  }
}

.select-badge-container {
  align-self: end;
}

.top-overlay {
  position: absolute;
  top: 0;
  justify-content: space-between;
  width: 100%;
  display: flex;
}

@include media-breakpoint-up(lg) {
  .badge-listing-type {
    margin-bottom: 8px;
  }

  .best-match-banner {
    border-radius: 4px;

    p,
    .details-container {
      font-size: 16px;
    }

    .details-container {
      padding: 16px;
    }

    .product-card-deal-banner,
    .product-image {
      border-radius: 0;
    }
  }

  .best-match-badge {
    position: absolute;
    top: 0;
    right: 0;
    margin: 1rem;
  }

  .downloads-container {
    margin-left: 24px;

    ::v-deep {
      .taco-rating {
        height: auto;
        top: 0;
        width: auto;

        &::before {
          display: none;
        }
      }
    }
  }

  .image-wrapper {
    max-width: 375px;
  }

  .pricing-text {
    font-size: 24px;
    line-height: 36px;
  }

  .plan-text {
    font-size: 16px;
  }

  .select-badge-container {
    align-self: baseline;
  }
}

@include media-breakpoint-up(xl) {
  .section-title h3 {
    width: 70%;
  }
}
</style>
